<template>
  <div class="page-container">
    <!-- 搜索表单 -->
    <div class="search-form">
      <el-form :inline="true" :model="searchForm" class="demo-form-inline">
        <el-form-item label="关键词">
          <el-input
            v-model="searchForm.keyword"
            placeholder="请输入管理员账号或昵称"
            clearable
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="状态">
          <el-select
            v-model="searchForm.status"
            placeholder="请选择状态"
            clearable
          >
            <el-option label="正常" :value="0" />
            <el-option label="禁用" :value="1" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 工具栏 -->
    <div class="toolbar">
      <div class="toolbar-left">
        <el-button type="primary" icon="el-icon-plus" @click="handleCreate">
          新增管理员
        </el-button>
      </div>
      <div class="toolbar-right">
        <el-button icon="el-icon-refresh" @click="loadData">刷新</el-button>
      </div>
    </div>

    <!-- 表格 -->
    <el-table
      v-loading="loading"
      :data="tableData"
      :max-height="tableMaxHeight"
      style="width: 100%"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="55" />
      <el-table-column prop="adminName" label="管理员账号" width="120" />
      <el-table-column prop="nickName" label="昵称" width="120" />
      <el-table-column prop="email" label="邮箱" width="180" />
      <el-table-column prop="phone" label="手机号" width="120" />
      <el-table-column prop="status" label="状态" width="80">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status === 0 ? 'success' : 'danger'">
            {{ scope.row.status | statusText }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="permissions" label="菜单权限" width="200">
        <template slot-scope="scope">
          <el-tag
            v-for="permission in scope.row.permissions"
            :key="permission"
            size="mini"
            style="margin-right: 5px"
          >
            {{ getPermissionText(permission) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="lastLoginTime" label="最后登录" width="150">
        <template slot-scope="scope">
          {{ scope.row.lastLoginTime | formatDate }}
        </template>
      </el-table-column>
      <el-table-column prop="createdTime" label="创建时间" width="150">
        <template slot-scope="scope">
          {{ scope.row.createdTime | formatDate }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200" fixed="right">
        <template slot-scope="scope">
          <div class="table-actions">
            <el-button size="mini" @click="handleView(scope.row)"
              >详情</el-button
            >
            <el-button size="mini" type="primary" @click="handleEdit(scope.row)"
              >编辑</el-button
            >
            <el-dropdown
              @command="(command) => handleCommand(command, scope.row)"
            >
              <el-button size="mini">
                更多<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item
                  :command="`${scope.row.status === 0 ? 'disable' : 'enable'}`"
                >
                  {{ scope.row.status === 0 ? "禁用" : "启用" }}
                </el-dropdown-item>
                <el-dropdown-item command="resetPassword"
                  >重置密码</el-dropdown-item
                >
                <el-dropdown-item command="delete" divided
                  >删除</el-dropdown-item
                >
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.size"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
      />
    </div>

    <!-- 新增/编辑对话框 -->
    <admin-form
      :visible.sync="formVisible"
      :admin-data="currentAdmin"
      @success="handleFormSuccess"
    />

    <!-- 详情对话框 -->
    <admin-detail :visible.sync="detailVisible" :admin-data="currentAdmin" />
  </div>
</template>

<script>
import {
  getAdminList,
  deleteAdmin,
  changeAdminStatus,
  resetAdminPassword,
} from "@/api/admin";
import AdminForm from "./components/AdminForm";
import AdminDetail from "./components/AdminDetail";

export default {
  name: "AdminList",
  components: {
    AdminForm,
    AdminDetail,
  },
  data() {
    return {
      loading: false,
      tableData: [],
      tableMaxHeight: 400,
      selectedRows: [],
      searchForm: {
        keyword: "",
        status: null,
      },
      pagination: {
        page: 1,
        size: 20,
        total: 0,
      },
      formVisible: false,
      detailVisible: false,
      currentAdmin: null,
    };
  },
  created() {
    this.loadData();
  },
  mounted() {
    this.updateTableMaxHeight();
    window.addEventListener("resize", this.updateTableMaxHeight);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateTableMaxHeight);
  },
  methods: {
    updateTableMaxHeight() {
      this.$nextTick(() => {
        const tableEl = this.$el && this.$el.querySelector(".el-table");
        if (!tableEl) return;

        const top = tableEl.getBoundingClientRect().top;
        const bottomReserve = 24;
        const minHeight = 240;
        this.tableMaxHeight = Math.max(
          minHeight,
          Math.floor(window.innerHeight - top - bottomReserve),
        );
      });
    },
    async loadData() {
      this.loading = true;
      try {
        const params = {
          ...this.searchForm,
          page: this.pagination.page,
          size: this.pagination.size,
        };
        const response = await getAdminList(params);
        this.tableData = response.data.records || response.data;
        this.pagination.total = response.data.total || this.tableData.length;
      } catch (error) {
        console.error("加载数据失败:", error);
      } finally {
        this.loading = false;
        this.updateTableMaxHeight();
      }
    },

    handleSearch() {
      this.pagination.page = 1;
      this.loadData();
    },

    resetSearch() {
      this.searchForm = {
        keyword: "",
        status: null,
      };
      this.pagination.page = 1;
      this.loadData();
    },

    handleCreate() {
      this.currentAdmin = null;
      this.formVisible = true;
    },

    handleView(row) {
      this.currentAdmin = row;
      this.detailVisible = true;
    },

    handleEdit(row) {
      this.currentAdmin = row;
      this.formVisible = true;
    },

    async handleCommand(command, row) {
      switch (command) {
        case "enable":
        case "disable":
          await this.handleStatusChange(row, command === "enable" ? 0 : 1);
          break;
        case "resetPassword":
          await this.handleResetPassword(row);
          break;
        case "delete":
          await this.handleDelete(row);
          break;
      }
    },

    async handleStatusChange(row, status) {
      try {
        await this.$confirm(
          `确定要${status === 0 ? "启用" : "禁用"}管理员"${row.nickName}"吗？`,
          "提示",
          {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            type: "warning",
          },
        );

        await changeAdminStatus(row.id, status);
        this.$message.success("操作成功");
        this.loadData();
      } catch (error) {
        if (error !== "cancel") {
          console.error("状态变更失败:", error);
        }
      }
    },

    async handleResetPassword(row) {
      try {
        const { value: newPassword } = await this.$prompt(
          "请输入新密码",
          "重置密码",
          {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            inputValidator: (value) => {
              if (!value || value.length < 6) {
                return "密码不能少于6位";
              }
              return true;
            },
          },
        );

        await resetAdminPassword(row.id, newPassword);
        this.$message.success("密码重置成功");
      } catch (error) {
        if (error !== "cancel") {
          console.error("密码重置失败:", error);
        }
      }
    },

    async handleDelete(row) {
      try {
        await this.$confirm(
          `确定要删除管理员"${row.nickName}"吗？此操作不可恢复！`,
          "警告",
          {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            type: "warning",
          },
        );

        await deleteAdmin(row.id);
        this.$message.success("删除成功");
        this.loadData();
      } catch (error) {
        if (error !== "cancel") {
          console.error("删除失败:", error);
        }
      }
    },

    handleSelectionChange(selection) {
      this.selectedRows = selection;
    },

    handleSizeChange(size) {
      this.pagination.size = size;
      this.loadData();
    },

    handleCurrentChange(page) {
      this.pagination.page = page;
      this.loadData();
    },

    handleFormSuccess() {
      this.formVisible = false;
      this.loadData();
    },

    getPermissionText(permission) {
      const permissionMap = {
        SYSTEM_MANAGE: "管理员管理",
        USER_MANAGE: "用户管理",
        GROUP_MANAGE: "群组管理",
      };
      return permissionMap[permission] || permission;
    },
  },
};
</script>

<style lang="scss" scoped>
.pagination {
  margin-top: 20px;
  text-align: center;
}
</style>
